import React from 'react';
import { Link } from 'react-router-dom';
import { MapleLeaf } from '@/components/common/MapleLeaf';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Brain, Cpu, Server, Rocket, BookOpen, GitBranch, ShieldCheck, Leaf, Database, Sparkles } from 'lucide-react';
import SEO from '@/components/common/SEO';
import MaplePTQuickChatModal from '@/components/maplept/MaplePTQuickChatModal';
import MaplePTCollaborationSection from '@/components/maplept/MaplePTCollaborationSection';
import Header from '@/components/layout/Header';
import MainLayout from '@/components/layout/MainLayout';

const StatItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col items-start">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-base font-semibold">{value}</span>
  </div>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{children}</h2>
);

const SubTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-xl md:text-2xl font-semibold">{children}</h3>
);

const MaplePT: React.FC = () => {
  return (
    <MainLayout>
      <div className="min-h-screen bg-background text-foreground">
        <SEO
          title="MaplePT is Canada's Sovereign Language Model"
          description="MaplePT is a Canadian-built, Canadian-hosted language model optimized for instruction following, privacy, and sustainability."
          imageUrl="/images/maplept-syrup.png"
          url="/maplept"
        />

        <Header />

        {/* Hero */}
        <header className="relative min-h-[60vh] sm:min-h-[70vh] lg:min-h-[80vh] flex items-center justify-center text-center">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('/images/maplept-hero.jpg')` } as React.CSSProperties}
          />
          {/* Strong contrast overlay + subtle vertical gradient */}
          <div className="absolute inset-0 bg-black/65" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />

          <div className="container relative z-10 mx-auto px-4">
            <div className="mx-auto max-w-4xl rounded-2xl border border-white/10 bg-black/30 backdrop-blur-md p-8 shadow-2xl">
              <MapleLeaf size={80} className="text-primary mb-6 mx-auto drop-shadow-md" />
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-white drop-shadow-lg">
                MaplePT is Canada&apos;s Sovereign Language Model
              </h1>
              <p className="mt-4 max-w-3xl mx-auto text-lg md:text-xl text-white/80">
                Built from Canadian data. Trained on Canadian infrastructure. <br></br>Tuned for Canadian values.
              </p>

              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <Badge className="bg-white/10 text-white border-white/20">Made in Canada </Badge>
                <Badge className="bg-white/10 text-white border-white/20">Data Sovereign</Badge>
                <Badge className="bg-white/10 text-white border-white/20">Sustainably Trained</Badge>
              </div>

              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <MaplePTQuickChatModal />
                <Button asChild variant="outline" className="border-white/30 text-black dark:text-white hover:bg-white/10">
                  <a href="https://doi.org/10.36227/techrxiv.176162061.19169845/v1" target="_blank" rel="noopener noreferrer">
                    Read the Paper
                  </a>
                </Button>
                <Button asChild variant="ghost" className="text-white hover:bg-white/10">
                  <a href="#get-the-model">Get the Model</a>
                </Button>
              </div>

              {/* Styled DOI for better legibility */}
              <p className="text-xs mt-4 text-white">
                DOI:{' '}
                <a
                  href="https://doi.org/10.36227/techrxiv.176162061.19169845/v1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline underline-offset-2 decoration-white/60 hover:decoration-white"
                >
                  10.36227/techrxiv.176162061.19169845/v1
                </a>
              </p>

              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-left text-white/80">
                <StatItem label="Parameters" value="~4B" />
                <StatItem label="Context Window" value="4096 tokens" />
                <StatItem label="Optimized For" value="Instruction Following" />
                <StatItem label="License" value="MIT (Open)" />
              </div>
            </div>
          </div>
        </header>

        {/* Narrative */}
        <section className="container mx-auto px-4 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <SectionTitle>Why MaplePT?</SectionTitle>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                MaplePT was built as a statement — that advanced Canadian AI doesn't need to be hosted or trained on foreign infrastructure; it doesn't need to be environmentally wasteful. 
                It proves Canada can lead in ethical, local, and sustainable AI innovation.
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="text-primary" />
                  <div>
                    <SubTitle>Sovereignty</SubTitle>
                    <p className="text-muted-foreground">
                      Training data curated in-house and aligned with Canadian public knowledge frameworks.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Cpu className="text-primary" />
                  <div>
                    <SubTitle>Accessibility</SubTitle>
                    <p className="text-muted-foreground">
                      Runs efficiently on consumer GPUs — trained across distributed RTX 4080 and 3090 systems.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Leaf className="text-primary" />
                  <div>
                    <SubTitle>Sustainability</SubTitle>
                    <p className="text-muted-foreground">
                      Low-carbon fine-tuning and small-model optimization — no mega-datacenters required.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="overflow-hidden rounded-xl shadow-lg border-0">
              <div className="relative aspect-[16/11]">
                <img
                  src="/images/maplept-syrup.png"
                  alt="MaplePT Mini v1 — Sovereign Canadian AI"
                  className="absolute inset-0 h-full w-full object-cover object-bottom"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6 text-white">
                  <h3 className="text-2xl font-semibold">MaplePT Mini v1</h3>
                  <p className="text-sm text-white/90">Built in Canada. Open. Sovereign.</p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Technical Methodology */}
        <section className="border-t border-b bg-card">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <SectionTitle>How We Built It</SectionTitle>
            <p className="mt-3 text-muted-foreground max-w-3xl">
              A practical, transparent approach that balances performance, cost, and sovereignty.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Brain className="text-primary" />
                    <SubTitle>Architecture</SubTitle>
                  </div>
                  <p className="text-muted-foreground">
                    Based on a modified Phi-3-style architecture tuned for instruction following and knowledge retention.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Server className="text-primary" />
                    <SubTitle>Training Infrastructure</SubTitle>
                  </div>
                  <p className="text-muted-foreground">
                    Distributed training across low-cost GPUs using PyTorch + Accelerate + QLoRA, optimizing for energy and cost.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Database className="text-primary" />
                    <SubTitle>Datasets</SubTitle>
                  </div>
                  <p className="text-muted-foreground">
                    Balanced Canadian English and French corpus, aligned to public, educational, and government sources.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <ShieldCheck className="text-primary" />
                    <SubTitle>Alignment</SubTitle>
                  </div>
                  <p className="text-muted-foreground">
                    Fine-tuned with custom identity and refusal datasets to strengthen factuality, civility, and bias resistance.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Availability */}
        <section id="get-the-model" className="container mx-auto px-4 py-12 md:py-16">
          <SectionTitle>Available for Everyone</SectionTitle>
          <p className="mt-3 text-muted-foreground max-w-3xl">
            Explore the open release and deploy MaplePT locally or on-prem.
          </p>
          <p className="text-xs mt-4">
          DOI:{' '}
          <a
            href="https://doi.org/10.36227/techrxiv.176162061.19169845/v1"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 decoration-white/60 hover:decoration-white"
          >
            10.36227/techrxiv.176162061.19169845/v1
          </a>
        </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="group">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className="text-primary" />
                  <SubTitle>Hugging Face</SubTitle>
                </div>
                <p className="text-muted-foreground mb-4">
                  Explore the open-source release and model card.
                </p>
                <div className="flex gap-2 mb-4">
                  <Badge variant="outline">MIT License</Badge>
                  <Badge variant="outline">Model Card</Badge>
                  <Badge variant="outline">Datasets Included</Badge>
                </div>
                <Button asChild variant="link" className="p-0">
                  <a href="https://huggingface.co/canxp-ai/canxpai-maplept-mini-v1" target="_blank" rel="noopener noreferrer">
                    Visit Hugging Face →
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="group">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Rocket className="text-primary" />
                  <SubTitle>Docker Image (vLLM)</SubTitle>
                </div>
                <p className="text-muted-foreground mb-4">
                  Run MaplePT locally or on-prem with an OpenAI-compatible API endpoint.
                </p>

                <div className="rounded-md border bg-muted p-4 text-sm overflow-auto">
                  <pre className="whitespace-pre-wrap break-words"><code>
docker pull canxpai/maplept-mini-v1-vllm:latest
docker run -p 8686:8686 canxpai/maplept-mini-v1-vllm
                  </code></pre>
                </div>

                <Button asChild variant="link" className="p-0 mt-4">
                  <a href="https://hub.docker.com/r/canxpai/maplept-mini-v1-vllm" target="_blank" rel="noopener noreferrer">
                    View on Docker Hub →
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Research Ethos */}
        <section className="border-t bg-card">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <SectionTitle>At the Edge of Convenience and Conscience</SectionTitle>
            <blockquote className="mt-4 border-l-4 pl-4 text-muted-foreground italic max-w-3xl">
              "MaplePT is more than just another AI model — it's a case study in building AI at the edge of convenience and conscience of Canadians."
            </blockquote>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6">
                  <SubTitle>Transparency</SubTitle>
                  <p className="text-muted-foreground mt-2">
                    All experiments reproducible — full model card and training corupus available upon approval.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <SubTitle>Collaboration</SubTitle>
                  <p className="text-muted-foreground mt-2">
                    Encouraging universities and startups to extend MaplePT for domain-specific applications.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <SubTitle>Innovation</SubTitle>
                  <p className="text-muted-foreground mt-2">
                    Demonstrating how small-scale AI can lead to big-impact national innovation.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Collaboration section */}
        <MaplePTCollaborationSection />

        {/* CTA */}
        <section className="border-t bg-gradient-to-br from-primary/10 to-primary/5">
          <div className="container mx-auto px-4 py-12 md:py-16 text-center">
            <SectionTitle>Join the Sovereign AI Movement</SectionTitle>
            <p className="mt-3 text-muted-foreground max-w-3xl mx-auto">
              Sovereign AI isn't about isolation — it's about empowerment. Let's redefine what it means to build intelligence on Canadian terms.
            </p>

            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <Button asChild>
                <Link to="/forums">Join the MapleOS Forums</Link>
              </Button>
              <Button asChild variant="outline">
                <a href="https://doi.org/10.36227/techrxiv.176162061.19169845/v1" target="_blank" rel="noopener noreferrer">
                  Read the MaplePT Whitepaper
                </a>
              </Button>
              <Button asChild variant="ghost">
                <Link to="/partnership">Join our Partnership</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Footer note */}
        <footer className="border-t">
          <div className="container mx-auto px-4 py-6 text-center text-xs text-muted-foreground">
            © 2026 mapleos · Made in Canada · #SovereignAI
          </div>
        </footer>
      </div>
    </MainLayout>
  );
};

export default MaplePT;